import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  RadioTower, PlayCircle, Send, UserPlus, CheckCircle2, XCircle, Loader2, Clock, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useBrand } from "@/context/BrandContext";
import { useT } from "@/i18n";
import { formatDateTime } from "@/lib/format";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { StatusChip } from "@/components/common/StatusChip";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const SEV_TONE = { alta: "red", media: "amber", baja: "gray" };
const PULSO_ESTADOS = ["nueva", "asignada", "aceptada", "en_curso", "en_revision", "en_correccion", "aprobada"];

const waitingFor = (iso, t) => {
  if (!iso) return "—";
  const hours = Math.floor((Date.now() - new Date(iso).getTime()) / 3600000);
  if (hours < 1) return t("tower.minutes_ago", { n: Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 60000)) });
  if (hours < 48) return t("tower.hours_ago", { n: hours });
  return t("tower.days_ago", { n: Math.floor(hours / 24) });
};

export default function TowerPage() {
  const { t } = useT();
  const { brandParam } = useBrand();
  const navigate = useNavigate();
  const [regla, setRegla] = useState("all");
  const [soloHumanas, setSoloHumanas] = useState(false);
  const [busy, setBusy] = useState(null);
  const [dismiss, setDismiss] = useState(null);
  const [motivo, setMotivo] = useState("");

  const summary = useQuery({
    queryKey: ["tower-summary", brandParam],
    refetchInterval: 30000,
    queryFn: async () => (await api.get("/control-tower/summary", { params: { brand: brandParam } })).data,
  });

  // Fix L (2026-09-28): tira de resumen del día arriba de la cola de alertas.
  const pulso = useQuery({
    queryKey: ["tower-pulso", brandParam],
    refetchInterval: 30000,
    queryFn: async () => (await api.get("/control-tower/pulso", { params: { brand: brandParam } })).data,
  });

  const tasks = useQuery({
    queryKey: ["tower-tasks", brandParam, regla, soloHumanas],
    refetchInterval: 30000,
    queryFn: async () =>
      (await api.get("/control-tower/tasks", {
        params: { brand: brandParam, regla: regla === "all" ? undefined : regla,
                  solo_humanas: soloHumanas || undefined },
      })).data,
  });

  const refresh = () => {
    summary.refetch();
    pulso.refetch();
    tasks.refetch();
  };

  const act = async (task, action) => {
    setBusy(`${task.id}-${action}`);
    try {
      if (action === "notify") {
        await api.post(`/control-tower/tasks/${task.id}/notify`);
        toast.success(t("tower.notified"));
      } else if (action === "resolve") {
        await api.post(`/control-tower/tasks/${task.id}/resolve`, {});
        toast.success(t("tower.resolved"));
      }
      refresh();
    } catch (e) {
      toast.error(apiError(e, t("app.error")));
    } finally {
      setBusy(null);
    }
  };

  const runNow = async () => {
    setBusy("run");
    try {
      const { data } = await api.post("/control-tower/run");
      toast.success(t("tower.run_done", { n: data.nuevas || 0, a: data.auto_resueltas || 0 }));
      refresh();
    } catch (e) {
      toast.error(apiError(e, t("app.error")));
    } finally {
      setBusy(null);
    }
  };

  const confirmDismiss = async () => {
    setBusy("dismiss");
    try {
      await api.post(`/control-tower/tasks/${dismiss.id}/dismiss`, { motivo });
      toast.success(t("tower.dismissed"));
      setDismiss(null);
      setMotivo("");
      refresh();
    } catch (e) {
      toast.error(apiError(e, t("app.error")));
    } finally {
      setBusy(null);
    }
  };

  const items = tasks.data?.items || [];
  const s = summary.data || {};
  const p = pulso.data;

  return (
    <>
      <PageHeader
        title={t("tower.title")}
        subtitle={t("tower.subtitle")}
        testId="tower-header"
        actions={
          <Button data-testid="tower-run-now" disabled={busy === "run"} onClick={runNow}
            className="h-8 rounded-[6px] bg-ink text-[13px] hover:opacity-90">
            {busy === "run" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <PlayCircle className="mr-1.5 h-3.5 w-3.5" />}
            {t("tower.run_now")}
          </Button>
        }
      />

      {p && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-line bg-white px-6 py-2.5"
          data-testid="tower-pulse">
          <div className="flex flex-wrap items-center gap-1.5">
            {PULSO_ESTADOS.map((st) => (
              <span key={st} data-testid={`tower-pulse-${st}`}
                className="inline-flex items-center gap-1.5 rounded-[6px] border border-line px-2 py-[3px] text-[11.5px] text-ink-secondary">
                {t(`woStatus.${st}`)}
                <span className="font-semibold tabular-nums text-ink">{p.por_estado?.[st] ?? 0}</span>
              </span>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <StatusChip tone="green" testId="tower-pulse-green" label={`${p.semaforo?.verde ?? 0} ${t("tower.pulse.on_time")}`} />
            <StatusChip tone="amber" testId="tower-pulse-amber" label={`${p.semaforo?.ambar ?? 0} ${t("tower.pulse.at_risk")}`} />
            <StatusChip tone="red" testId="tower-pulse-red" label={`${p.semaforo?.rojo ?? 0} ${t("tower.pulse.late")}`} />
            {!!p.estancadas_48h && (
              <span data-testid="tower-pulse-stale"
                className="inline-flex animate-pulse items-center rounded-[6px] bg-state-critical px-2 py-[3px] text-[11.5px] font-semibold text-white">
                {t("tower.pulse.stale", { n: p.estancadas_48h })}
              </span>
            )}
          </div>
          <span className="text-[11.5px] text-ink-tertiary" data-testid="tower-pulse-today">
            {t("tower.pulse.today", { c: p.citas_hoy ?? 0, l: p.llegaron_hoy ?? 0 })}
          </span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 border-b border-line bg-white px-6 py-2.5">
        <div className="mr-2 flex flex-wrap items-center gap-1.5" data-testid="tower-counters">
          <StatusChip tone="red" testId="tower-count-open" label={`${s.abiertas ?? 0} ${t("tower.open")}`} />
          <StatusChip tone="amber" testId="tower-count-human" label={`${s.requieren_humano ?? 0} ${t("tower.human")}`} />
          {Object.entries(s.por_regla || {}).map(([k, v]) => (
            <button key={k} data-testid={`tower-rule-${k}`} onClick={() => setRegla(k)}
              className="inline-flex items-center gap-1.5 rounded-[6px] border border-line px-2 py-[3px] text-[11.5px] text-ink-secondary hover:border-line-strong">
              {t(`tower.rules.${k}`)} <span className="tabular-nums font-semibold">{v}</span>
            </button>
          ))}
        </div>
        <Select value={regla} onValueChange={setRegla}>
          <SelectTrigger data-testid="tower-rule-filter" className="h-8 w-[230px] rounded-[6px] text-[13px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("tower.all_rules")}</SelectItem>
            {(s.reglas || []).map((r) => (
              <SelectItem key={r} value={r}>{t(`tower.rules.${r}`)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant={soloHumanas ? "default" : "outline"} data-testid="tower-only-human"
          onClick={() => setSoloHumanas((v) => !v)}
          className={soloHumanas ? "h-8 rounded-[6px] bg-ink text-[12.5px]" : "h-8 rounded-[6px] text-[12.5px]"}>
          {t("tower.only_human")}
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto bg-surface-sunken p-5" data-testid="tower-page">
        {tasks.isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-[92px] rounded-[10px]" />)}
          </div>
        ) : !items.length ? (
          <EmptyState icon={RadioTower} testId="tower-empty" title={t("tower.empty_title")} description={t("tower.empty_desc")} />
        ) : (
          <ul className="space-y-2.5" data-testid="tower-queue">
            {items.map((task) => (
              <li key={task.id} data-testid={`tower-task-${task.id}`}
                className="rounded-[10px] border border-line bg-white p-4 transition-shadow hover:shadow-[0_2px_8px_rgba(15,23,42,0.06)]">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <StatusChip tone={SEV_TONE[task.severidad] || "gray"} label={t(`tower.severity.${task.severidad}`)} />
                      <span className="text-[13.5px] font-semibold text-ink">{t(`tower.rules.${task.regla}`)}</span>
                      {task.requiere_humano && (
                        <StatusChip tone="amber" testId={`tower-human-${task.id}`} label={t("tower.needs_human")} />
                      )}
                      <span className="inline-flex items-center gap-1 text-[11.5px] text-ink-tertiary">
                        <Clock className="h-3 w-3" /> {waitingFor(task.first_detected_at, t)}
                      </span>
                      <span className="text-[11.5px] text-ink-tertiary">{t("tower.step")} {task.paso}/3</span>
                    </div>
                    <p className="mt-1.5 text-[12.5px] leading-snug text-ink-secondary">{task.detalle}</p>
                    <p className="mt-1 text-[12px] text-ink-tertiary">
                      {task.wo_code && (
                        <button data-testid={`tower-go-wo-${task.id}`} onClick={() => navigate(`/ordenes/${task.work_order_id}`)}
                          className="inline-flex items-center gap-1 font-medium text-brand hover:underline">
                          {task.wo_code} <ExternalLink className="h-3 w-3" />
                        </button>
                      )}
                      {task.link && (
                        <button data-testid={`tower-go-link-${task.id}`} onClick={() => navigate(task.link)}
                          className="inline-flex items-center gap-1 font-medium text-brand hover:underline">
                          {t("tower.open_inbox")} <ExternalLink className="h-3 w-3" />
                        </button>
                      )}
                      {task.property_nombre ? ` · ${task.property_nombre}` : ""}
                      {task.sub_nombre ? ` · ${task.sub_nombre}` : ""}
                      {task.assigned_nombre ? ` · ${t("tower.assigned_to")} ${task.assigned_nombre}` : ""}
                    </p>
                    {!!task.pasos?.length && (
                      <ul className="mt-2 space-y-0.5">
                        {task.pasos.map((p, i) => (
                          <li key={i} className="text-[11.5px] text-ink-tertiary">
                            {t("tower.step")} {p.n}: {p.detalle} · {formatDateTime(p.at)}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="flex shrink-0 flex-wrap gap-1.5">
                    {task.work_order_id && (
                      <>
                        <Button variant="outline" size="sm" data-testid={`tower-notify-${task.id}`}
                          disabled={busy === `${task.id}-notify`} onClick={() => act(task, "notify")}
                          className="h-7 rounded-[6px] text-[11.5px]">
                          <Send className="mr-1 h-3 w-3" /> {t("tower.actions.notify")}
                        </Button>
                        <Button variant="outline" size="sm" data-testid={`tower-reassign-${task.id}`}
                          onClick={() => navigate(`/ordenes/${task.work_order_id}?assign=1`)}
                          className="h-7 rounded-[6px] text-[11.5px]">
                          <UserPlus className="mr-1 h-3 w-3" /> {t("tower.actions.reassign")}
                        </Button>
                      </>
                    )}
                    <Button variant="outline" size="sm" data-testid={`tower-resolve-${task.id}`}
                      disabled={busy === `${task.id}-resolve`} onClick={() => act(task, "resolve")}
                      className="h-7 rounded-[6px] border-state-ok-soft text-[11.5px] text-state-ok">
                      <CheckCircle2 className="mr-1 h-3 w-3" /> {t("tower.actions.resolve")}
                    </Button>
                    <Button variant="outline" size="sm" data-testid={`tower-dismiss-${task.id}`}
                      onClick={() => setDismiss(task)}
                      className="h-7 rounded-[6px] border-state-critical-soft text-[11.5px] text-state-critical">
                      <XCircle className="mr-1 h-3 w-3" /> {t("tower.actions.dismiss")}
                    </Button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Dialog open={!!dismiss} onOpenChange={(v) => !v && setDismiss(null)}>
        <DialogContent data-testid="tower-dismiss-dialog" className="max-w-[460px] rounded-[10px]">
          <DialogHeader>
            <DialogTitle className="text-[16px]">{t("tower.dismiss_title")}</DialogTitle>
          </DialogHeader>
          <Textarea data-testid="tower-dismiss-motivo" value={motivo} rows={3}
            onChange={(e) => setMotivo(e.target.value)} placeholder={t("tower.dismiss_ph")}
            className="rounded-[6px] text-[13px]" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDismiss(null)} className="h-9 rounded-[6px] text-[13px]">
              {t("app.cancel")}
            </Button>
            <Button data-testid="tower-dismiss-confirm" disabled={motivo.trim().length < 5 || busy === "dismiss"}
              onClick={confirmDismiss}
              className="h-9 rounded-[6px] bg-state-critical text-[13px] hover:opacity-90">
              {t("tower.actions.dismiss")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
